import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LogIn, AlertCircle } from 'lucide-react';
const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const {
    login,
    isAuthenticated
  } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    // If already authenticated, redirect to courses page
    if (isAuthenticated) {
      navigate('/courses');
    }
  }, [isAuthenticated, navigate]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setStatusMessage('Logging in...');
    setIsLoading(true);
    try {
      const {
        error: loginError
      } = await login(email, password);
      if (loginError) {
        setError(loginError);
        setStatusMessage('');
      } else {
        setStatusMessage('Logged in successfully!');
        navigate('/courses');
      }
    } catch (err) {
      setError('Failed to log in. Please check your credentials.');
      setStatusMessage('');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8" data-id="element-1069">
      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-1070">
        <div className="text-center" data-id="element-1071">
          <h2 className="text-3xl font-bold text-gray-800" data-id="element-1072">Welcome back</h2>
          <p className="mt-2 text-gray-600" data-id="element-1073">
            Sign in to your GoABitFurther Africa Academy account
          </p>
        </div>
        <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10" data-id="element-1074">
          {error && <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6" data-id="element-1075">
              <div className="flex" data-id="element-1076">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2" data-id="element-1077" />
                <p className="text-sm text-red-700" data-id="element-1078">{error}</p>
              </div>
            </div>}
          {statusMessage && !error && <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6" data-id="element-1079">
              <p className="text-sm text-blue-700" data-id="element-1080">{statusMessage}</p>
            </div>}
          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-1081">
            <div data-id="element-1082">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-1083">
                Email address
              </label>
              <div className="mt-1" data-id="element-1084">
                <input id="email" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1085" />
              </div>
            </div>
            <div data-id="element-1086">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700" data-id="element-1087">
                Password
              </label>
              <div className="mt-1" data-id="element-1088">
                <input id="password" name="password" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)} className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1089" />
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-1090">
              <div className="flex items-center" data-id="element-1091">
                <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1092" />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700" data-id="element-1093">
                  Remember me
                </label>
              </div>
              <div className="text-sm" data-id="element-1094">
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-1095">
                  Forgot your password?
                </a>
              </div>
            </div>
            <div data-id="element-1096">
              <button type="submit" disabled={isLoading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300" data-id="element-1097">
                {isLoading ? <span data-id="element-1098">Signing in...</span> : <>
                    <LogIn className="h-5 w-5 mr-2" data-id="element-1099" />
                    Sign in
                  </>}
              </button>
            </div>
          </form>
          <div className="mt-6" data-id="element-1100">
            <div className="relative" data-id="element-1101">
              <div className="absolute inset-0 flex items-center" data-id="element-1102">
                <div className="w-full border-t border-gray-300" data-id="element-1103" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-1104">
                <span className="px-2 bg-white text-gray-500" data-id="element-1105">Or</span>
              </div>
            </div>
            <div className="mt-6 text-center" data-id="element-1106">
              <p className="text-sm text-gray-600" data-id="element-1107">
                Don't have an account?{' '}
                <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-1108">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
          <div className="mt-6 text-center" data-id="element-1109">
            <p id="status" className="text-xs text-gray-500" data-id="element-1110">
              {isAuthenticated ? 'Status: Logged in' : 'Status: Not logged in'}
            </p>
          </div>
        </div>
      </div>
    </div>;
};
export default Login;